import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import LandingPage from './pages/LandingPage';

const LandingPageCustomizer = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');
  const [showPreview, setShowPreview] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState('default');
  const [templates, setTemplates] = useState([]);
  
  // إعدادات التخصيص
  const [customization, setCustomization] = useState({
    // الإعدادات العامة
    general: {
      platform_name: 'منصة الأندية الرياضية',
      platform_logo: null,
      platform_favicon: null,
      primary_color: '#3b82f6',
      secondary_color: '#8b5cf6',
      accent_color: '#10b981',
      font_family: 'Cairo',
      custom_css: '',
      meta_title: 'منصة الأندية الرياضية - الحل الشامل لإدارة الأندية',
      meta_description: 'منصة شاملة لإدارة الأندية الرياضية مع جميع الأدوات المطلوبة'
    },
    
    // قسم Hero الرئيسي
    hero: {
      enabled: true,
      title: 'مرحباً بكم في منصة الأندية الرياضية',
      subtitle: 'الحل الشامل لإدارة الأندية الرياضية الحديثة',
      description: 'نوفر لكم جميع الأدوات المطلوبة لإدارة ناديكم بكفاءة واحترافية',
      background_type: 'gradient', // gradient, image, video
      background_image: null,
      background_video: null,
      background_gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      text_color: '#ffffff',
      button_text: 'ابدأ رحلتك معنا',
      button_color: '#ea580c',
      button_link: '/login',
      show_stats: true,
      stats: [
        { label: 'أندية مسجلة', value: '150+', icon: '🏟️' },
        { label: 'لاعب نشط', value: '5000+', icon: '⚽' },
        { label: 'مباراة منظمة', value: '2000+', icon: '🏆' },
        { label: 'مدينة', value: '25+', icon: '📍' }
      ]
    },
    
    // قسم الميزات
    features: {
      enabled: true,
      title: 'لماذا تختار منصتنا؟',
      subtitle: 'ميزات متقدمة تجعل إدارة ناديك أسهل وأكثر احترافية',
      layout: 'grid', // grid, carousel, list
      items: [
        {
          id: 1,
          title: 'إدارة شاملة للاعبين',
          description: 'نظام متكامل لإدارة معلومات اللاعبين وإحصائياتهم',
          icon: '👥',
          color: '#3b82f6'
        },
        {
          id: 2,
          title: 'جدولة المباريات',
          description: 'تنظيم المباريات والبطولات بسهولة مع إشعارات تلقائية',
          icon: '📅',
          color: '#8b5cf6'
        },
        {
          id: 3,
          title: 'تقارير وإحصائيات',
          description: 'تقارير مفصلة وإحصائيات شاملة لأداء النادي',
          icon: '📊',
          color: '#10b981'
        },
        {
          id: 4,
          title: 'إدارة مالية',
          description: 'متابعة الميزانية والمصروفات والإيرادات',
          icon: '💰',
          color: '#f59e0b'
        }
      ]
    },
    
    // قسم عرض الأندية
    clubs_showcase: {
      enabled: true,
      title: 'الأندية المميزة',
      subtitle: 'تعرف على بعض الأندية التي تثق بمنصتنا',
      layout: 'carousel', // grid, carousel
      show_count: 6,
      auto_slide: true,
      slide_duration: 5000
    },
    
    // قسم الشركاء
    partners: {
      enabled: true,
      title: 'شركاؤنا في النجاح',
      subtitle: 'نفتخر بثقة شركائنا ودعمهم المستمر',
      logos: [],
      layout: 'scroll', // grid, scroll
      background_color: '#f8fafc'
    },
    
    // قسم التواصل
    contact: {
      enabled: true,
      title: 'تواصل معنا',
      subtitle: 'نحن هنا لمساعدتك في بناء مستقبل رياضي مميز',
      email: 'info@platform.sa',
      phone: '+966 50 123 4567',
      address: 'الرياض، المملكة العربية السعودية',
      social_links: {
        twitter: '',
        instagram: '',
        linkedin: '',
        youtube: ''
      },
      contact_form: true,
      map_embed: ''
    },
    
    // Footer
    footer: {
      enabled: true,
      copyright: '© 2024 منصة الأندية الرياضية. جميع الحقوق محفوظة.',
      links: [
        { text: 'الشروط والأحكام', url: '/terms' },
        { text: 'سياسة الخصوصية', url: '/privacy' },
        { text: 'تواصل معنا', url: '/contact' }
      ],
      show_social: true,
      background_color: '#1f2937',
      text_color: '#ffffff'
    }
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // أقسام التخصيص
  const sections = [
    { id: 'general', name: 'إعدادات عامة', icon: '🎨', desc: 'الشعار والألوان والخطوط' },
    { id: 'hero', name: 'القسم الرئيسي', icon: '🚀', desc: 'العنوان والخلفية والإحصائيات' },
    { id: 'features', name: 'الميزات', icon: '⭐', desc: 'عرض ميزات المنصة' },
    { id: 'clubs', name: 'عرض الأندية', icon: '🏟️', desc: 'تخصيص عرض الأندية' },
    { id: 'partners', name: 'الشركاء', icon: '🤝', desc: 'شعارات وروابط الشركاء' },
    { id: 'contact', name: 'التواصل', icon: '📞', desc: 'معلومات التواصل والنموذج' },
    { id: 'footer', name: 'الفوتر', icon: '📄', desc: 'أسفل الصفحة والروابط' },
    { id: 'templates', name: 'القوالب', icon: '📚', desc: 'إدارة القوالب المحفوظة' }
  ];

  // تحميل الإعدادات الحالية
  useEffect(() => {
    fetchCurrentSettings();
    fetchTemplates();
  }, []);

  const fetchCurrentSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/landing-customization`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setCustomization(prev => ({ ...prev, ...response.data }));
        setCurrentTemplate(response.data.template_name || 'default');
      }
    } catch (error) {
      console.log('⚠️ استخدام الإعدادات الافتراضية:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/landing-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTemplates(response.data || [
        { id: 'default', name: 'افتراضي', description: 'التصميم الافتراضي للمنصة' },
        { id: 'modern', name: 'عصري', description: 'تصميم عصري ومتطور' },
        { id: 'classic', name: 'كلاسيكي', description: 'تصميم كلاسيكي وأنيق' }
      ]);
    } catch (error) {
      console.log('⚠️ استخدام القوالب الافتراضية:', error.message);
    }
  };

  // حفظ الإعدادات
  const saveCustomization = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const dataToSave = {
        ...customization,
        template_name: currentTemplate,
        updated_at: new Date().toISOString()
      };

      await axios.post(`${API_BASE_URL}/api/landing-customization`, dataToSave, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      showNotification('تم حفظ التخصيصات بنجاح! 🎉', 'success');
    } catch (error) {
      console.error('خطأ في حفظ التخصيصات:', error);
      showNotification('فشل في حفظ التخصيصات ❌', 'error');
    } finally {
      setSaving(false);
    }
  };

  // حفظ كقالب جديد
  const saveAsTemplate = async (templateName, description) => {
    try {
      const token = localStorage.getItem('token');
      
      const templateData = {
        name: templateName,
        description: description,
        settings: customization,
        created_at: new Date().toISOString()
      };

      await axios.post(`${API_BASE_URL}/api/landing-templates`, templateData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      showNotification(`تم حفظ القالب "${templateName}" بنجاح! 📚`, 'success');
      fetchTemplates(); // إعادة تحميل القوالب
    } catch (error) {
      console.error('خطأ في حفظ القالب:', error);
      showNotification('فشل في حفظ القالب ❌', 'error');
    }
  };

  // تحميل قالب محفوظ
  const loadTemplate = async (templateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/landing-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.settings) {
        setCustomization(response.data.settings);
        setCurrentTemplate(templateId);
        showNotification(`تم تحميل القالب بنجاح! 📚`, 'success');
      }
    } catch (error) {
      console.error('خطأ في تحميل القالب:', error);
      showNotification('فشل في تحميل القالب ❌', 'error');
    }
  };

  // رفع الملفات
  const handleFileUpload = async (file, type) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_BASE_URL}/api/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.file_id;
    } catch (error) {
      console.error('خطأ في رفع الملف:', error);
      throw error;
    }
  };

  // إظهار الإشعارات
  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500';
    notification.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-bounce`;
    notification.innerHTML = `<div class="flex items-center">${message}</div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 4000);
  };

  // تحديث الإعدادات
  const updateSetting = (section, key, value) => {
    setCustomization(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  // إضافة عنصر جديد (للميزات والشركاء)
  const addListItem = (section, newItem) => {
    setCustomization(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        items: [...(prev[section].items || []), { ...newItem, id: Date.now() }]
      }
    }));
  };

  // حذف عنصر
  const removeListItem = (section, itemId) => {
    setCustomization(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        items: prev[section].items.filter(item => item.id !== itemId)
      }
    }));
  };

  // Live Preview Component - عرض الصفحة العامة الحقيقية في iframe
  const LivePreview = () => {
    const [previewKey, setPreviewKey] = useState(Date.now());
    
    // تحديث المعاينة عند تغيير التخصيصات
    useEffect(() => {
      setPreviewKey(Date.now());
    }, [customization]);

    return (
      <div className="w-full h-full bg-white overflow-hidden">
        <div className="bg-blue-600 text-white px-4 py-2 text-center text-sm flex items-center justify-between">
          <span>📱 معاينة الصفحة العامة</span>
          <button
            onClick={() => {
              // تطبيق التخصيصات وإعادة تحميل المعاينة
              setPreviewKey(Date.now());
              showNotification('تم تحديث المعاينة! 🔄', 'success');
            }}
            className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs transition-colors"
          >
            🔄 تحديث المعاينة
          </button>
        </div>
        
        <iframe
          key={previewKey}
          src={`${window.location.origin}/?preview=true&theme=${encodeURIComponent(JSON.stringify(customization))}`}
          className="w-full h-full border-0"
          style={{ height: 'calc(100vh - 150px)' }}
          title="معاينة الصفحة العامة"
          onLoad={() => {
            console.log('✅ تم تحميل معاينة الصفحة العامة');
          }}
        />
      </div>
    );
  };

  // مكون الإعدادات العامة
  const GeneralSettings = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4">🎨 الإعدادات العامة</h3>
      
      {/* رفع الشعار */}
      <div className="bg-white rounded-xl p-6 border">
        <h4 className="font-bold mb-4">🖼️ شعار المنصة</h4>
        <div className="flex items-center space-x-4 space-x-reverse">
          {customization.general.platform_logo && (
            <img 
              src={`${API_BASE_URL}/api/files/${customization.general.platform_logo}`}
              alt="شعار المنصة"
              className="w-16 h-16 object-contain rounded-lg border"
            />
          )}
          <div className="flex-1">
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  try {
                    const fileId = await handleFileUpload(file, 'logo');
                    updateSetting('general', 'platform_logo', fileId);
                    showNotification('تم رفع الشعار بنجاح! 🖼️', 'success');
                  } catch (error) {
                    showNotification('فشل في رفع الشعار ❌', 'error');
                  }
                }
              }}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* الألوان الأساسية */}
      <div className="bg-white rounded-xl p-6 border">
        <h4 className="font-bold mb-4">🎨 الألوان الأساسية</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">اللون الأساسي</label>
            <div className="flex items-center space-x-2 space-x-reverse">
              <input
                type="color"
                value={customization.general.primary_color}
                onChange={(e) => updateSetting('general', 'primary_color', e.target.value)}
                className="w-12 h-10 rounded border"
              />
              <input
                type="text"
                value={customization.general.primary_color}
                onChange={(e) => updateSetting('general', 'primary_color', e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">اللون الثانوي</label>
            <div className="flex items-center space-x-2 space-x-reverse">
              <input
                type="color"
                value={customization.general.secondary_color}
                onChange={(e) => updateSetting('general', 'secondary_color', e.target.value)}
                className="w-12 h-10 rounded border"
              />
              <input
                type="text"
                value={customization.general.secondary_color}
                onChange={(e) => updateSetting('general', 'secondary_color', e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">لون التميز</label>
            <div className="flex items-center space-x-2 space-x-reverse">
              <input
                type="color"
                value={customization.general.accent_color}
                onChange={(e) => updateSetting('general', 'accent_color', e.target.value)}
                className="w-12 h-10 rounded border"
              />
              <input
                type="text"
                value={customization.general.accent_color}
                onChange={(e) => updateSetting('general', 'accent_color', e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* الخطوط */}
      <div className="bg-white rounded-xl p-6 border">
        <h4 className="font-bold mb-4">📝 نوع الخط</h4>
        <select
          value={customization.general.font_family}
          onChange={(e) => updateSetting('general', 'font_family', e.target.value)}
          className="w-full px-4 py-3 border rounded-lg"
        >
          <option value="Cairo">Cairo - القاهرة</option>
          <option value="Tajawal">Tajawal - تجول</option>
          <option value="Amiri">Amiri - الأميري</option>
          <option value="Almarai">Almarai - المرعى</option>
          <option value="Noto Sans Arabic">Noto Sans Arabic</option>
        </select>
      </div>

      {/* معلومات SEO */}
      <div className="bg-white rounded-xl p-6 border">
        <h4 className="font-bold mb-4">🔍 إعدادات SEO</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">عنوان الصفحة</label>
            <input
              type="text"
              value={customization.general.meta_title}
              onChange={(e) => updateSetting('general', 'meta_title', e.target.value)}
              className="w-full px-4 py-3 border rounded-lg"
              placeholder="عنوان الصفحة في نتائج البحث"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">وصف الصفحة</label>
            <textarea
              value={customization.general.meta_description}
              onChange={(e) => updateSetting('general', 'meta_description', e.target.value)}
              className="w-full px-4 py-3 border rounded-lg h-24"
              placeholder="وصف موجز للصفحة في نتائج البحث"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // مكون إعدادات Hero
  const HeroSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-800">🚀 القسم الرئيسي (Hero)</h3>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={customization.hero.enabled}
            onChange={(e) => updateSetting('hero', 'enabled', e.target.checked)}
            className="mr-2"
          />
          <span>تفعيل القسم</span>
        </label>
      </div>

      {customization.hero.enabled && (
        <>
          {/* النصوص */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">📝 النصوص</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الرئيسي</label>
                <input
                  type="text"
                  value={customization.hero.title}
                  onChange={(e) => updateSetting('hero', 'title', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الفرعي</label>
                <input
                  type="text"
                  value={customization.hero.subtitle}
                  onChange={(e) => updateSetting('hero', 'subtitle', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">الوصف</label>
                <textarea
                  value={customization.hero.description}
                  onChange={(e) => updateSetting('hero', 'description', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg h-24"
                />
              </div>
            </div>
          </div>

          {/* الخلفية */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">🖼️ الخلفية</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">نوع الخلفية</label>
                <select
                  value={customization.hero.background_type}
                  onChange={(e) => updateSetting('hero', 'background_type', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                >
                  <option value="gradient">تدرج لوني</option>
                  <option value="image">صورة</option>
                  <option value="video">فيديو</option>
                </select>
              </div>

              {customization.hero.background_type === 'gradient' && (
                <div>
                  <label className="block text-sm font-medium mb-2">كود التدرج (CSS)</label>
                  <input
                    type="text"
                    value={customization.hero.background_gradient}
                    onChange={(e) => updateSetting('hero', 'background_gradient', e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg font-mono text-sm"
                    placeholder="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  />
                </div>
              )}

              {customization.hero.background_type === 'image' && (
                <div>
                  <label className="block text-sm font-medium mb-2">صورة الخلفية</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        try {
                          const fileId = await handleFileUpload(file, 'background');
                          updateSetting('hero', 'background_image', fileId);
                          showNotification('تم رفع صورة الخلفية بنجاح! 🖼️', 'success');
                        } catch (error) {
                          showNotification('فشل في رفع الصورة ❌', 'error');
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              )}
            </div>
          </div>

          {/* زر الإجراء */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">🔘 زر الإجراء</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">نص الزر</label>
                <input
                  type="text"
                  value={customization.hero.button_text}
                  onChange={(e) => updateSetting('hero', 'button_text', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">لون الزر</label>
                <div className="flex items-center space-x-2 space-x-reverse">
                  <input
                    type="color"
                    value={customization.hero.button_color}
                    onChange={(e) => updateSetting('hero', 'button_color', e.target.value)}
                    className="w-12 h-10 rounded border"
                  />
                  <input
                    type="text"
                    value={customization.hero.button_color}
                    onChange={(e) => updateSetting('hero', 'button_color', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* الإحصائيات */}
          <div className="bg-white rounded-xl p-6 border">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold">📊 الإحصائيات</h4>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={customization.hero.show_stats}
                  onChange={(e) => updateSetting('hero', 'show_stats', e.target.checked)}
                  className="mr-2"
                />
                <span>إظهار الإحصائيات</span>
              </label>
            </div>
            
            {customization.hero.show_stats && (
              <div className="space-y-4">
                {customization.hero.stats.map((stat, index) => (
                  <div key={index} className="grid grid-cols-4 gap-3 items-center p-3 bg-gray-50 rounded-lg">
                    <input
                      type="text"
                      value={stat.icon}
                      onChange={(e) => {
                        const newStats = [...customization.hero.stats];
                        newStats[index].icon = e.target.value;
                        updateSetting('hero', 'stats', newStats);
                      }}
                      className="px-3 py-2 border rounded text-center"
                      placeholder="🏟️"
                    />
                    <input
                      type="text"
                      value={stat.label}
                      onChange={(e) => {
                        const newStats = [...customization.hero.stats];
                        newStats[index].label = e.target.value;
                        updateSetting('hero', 'stats', newStats);
                      }}
                      className="px-3 py-2 border rounded"
                      placeholder="التسمية"
                    />
                    <input
                      type="text"
                      value={stat.value}
                      onChange={(e) => {
                        const newStats = [...customization.hero.stats];
                        newStats[index].value = e.target.value;
                        updateSetting('hero', 'stats', newStats);
                      }}
                      className="px-3 py-2 border rounded"
                      placeholder="القيمة"
                    />
                    <button
                      onClick={() => {
                        const newStats = customization.hero.stats.filter((_, i) => i !== index);
                        updateSetting('hero', 'stats', newStats);
                      }}
                      className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
                
                <button
                  onClick={() => {
                    const newStat = { icon: '📊', label: 'إحصائية جديدة', value: '100+' };
                    updateSetting('hero', 'stats', [...customization.hero.stats, newStat]);
                  }}
                  className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
                >
                  ➕ إضافة إحصائية
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // مكون إعدادات الميزات
  const FeaturesSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-800">⭐ قسم الميزات</h3>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={customization.features.enabled}
            onChange={(e) => updateSetting('features', 'enabled', e.target.checked)}
            className="mr-2"
          />
          <span>تفعيل القسم</span>
        </label>
      </div>

      {customization.features.enabled && (
        <>
          {/* عنوان القسم */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">📝 عنوان القسم</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الرئيسي</label>
                <input
                  type="text"
                  value={customization.features.title}
                  onChange={(e) => updateSetting('features', 'title', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الفرعي</label>
                <textarea
                  value={customization.features.subtitle}
                  onChange={(e) => updateSetting('features', 'subtitle', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg h-20"
                />
              </div>
            </div>
          </div>

          {/* تخطيط العرض */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">📐 تخطيط العرض</h4>
            <select
              value={customization.features.layout}
              onChange={(e) => updateSetting('features', 'layout', e.target.value)}
              className="w-full px-4 py-3 border rounded-lg"
            >
              <option value="grid">شبكة (Grid)</option>
              <option value="carousel">كاروسيل (Carousel)</option>
              <option value="list">قائمة (List)</option>
            </select>
          </div>

          {/* الميزات */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">🎯 الميزات</h4>
            <div className="space-y-4">
              {customization.features.items.map((feature, index) => (
                <div key={feature.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-2">
                      <input
                        type="text"
                        value={feature.icon}
                        onChange={(e) => {
                          const newItems = [...customization.features.items];
                          newItems[index].icon = e.target.value;
                          updateSetting('features', 'items', newItems);
                        }}
                        className="w-full px-3 py-2 border rounded text-center"
                        placeholder="🎯"
                      />
                    </div>
                    <div className="col-span-3">
                      <input
                        type="text"
                        value={feature.title}
                        onChange={(e) => {
                          const newItems = [...customization.features.items];
                          newItems[index].title = e.target.value;
                          updateSetting('features', 'items', newItems);
                        }}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="عنوان الميزة"
                      />
                    </div>
                    <div className="col-span-4">
                      <input
                        type="text"
                        value={feature.description}
                        onChange={(e) => {
                          const newItems = [...customization.features.items];
                          newItems[index].description = e.target.value;
                          updateSetting('features', 'items', newItems);
                        }}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="وصف الميزة"
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="color"
                        value={feature.color}
                        onChange={(e) => {
                          const newItems = [...customization.features.items];
                          newItems[index].color = e.target.value;
                          updateSetting('features', 'items', newItems);
                        }}
                        className="w-full h-10 border rounded"
                      />
                    </div>
                    <div className="col-span-1">
                      <button
                        onClick={() => removeListItem('features', feature.id)}
                        className="w-full bg-red-500 text-white py-2 rounded hover:bg-red-600"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                onClick={() => addListItem('features', {
                  title: 'ميزة جديدة',
                  description: 'وصف الميزة الجديدة',
                  icon: '⭐',
                  color: customization.general.primary_color
                })}
                className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600"
              >
                ➕ إضافة ميزة جديدة
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // مكون إعدادات عرض الأندية
  const ClubsSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-800">🏟️ عرض الأندية</h3>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={customization.clubs_showcase.enabled}
            onChange={(e) => updateSetting('clubs_showcase', 'enabled', e.target.checked)}
            className="mr-2"
          />
          <span>تفعيل القسم</span>
        </label>
      </div>

      {customization.clubs_showcase.enabled && (
        <>
          {/* عنوان القسم */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">📝 عنوان القسم</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الرئيسي</label>
                <input
                  type="text"
                  value={customization.clubs_showcase.title}
                  onChange={(e) => updateSetting('clubs_showcase', 'title', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الفرعي</label>
                <textarea
                  value={customization.clubs_showcase.subtitle}
                  onChange={(e) => updateSetting('clubs_showcase', 'subtitle', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg h-20"
                />
              </div>
            </div>
          </div>

          {/* إعدادات العرض */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">🎛️ إعدادات العرض</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">نمط العرض</label>
                <select
                  value={customization.clubs_showcase.layout}
                  onChange={(e) => updateSetting('clubs_showcase', 'layout', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                >
                  <option value="grid">شبكة (Grid)</option>
                  <option value="carousel">كاروسيل متحرك</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">عدد الأندية المعروضة</label>
                <input
                  type="number"
                  min="3"
                  max="12"
                  value={customization.clubs_showcase.show_count}
                  onChange={(e) => updateSetting('clubs_showcase', 'show_count', parseInt(e.target.value))}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
            </div>
            
            {customization.clubs_showcase.layout === 'carousel' && (
              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={customization.clubs_showcase.auto_slide}
                    onChange={(e) => updateSetting('clubs_showcase', 'auto_slide', e.target.checked)}
                    className="mr-2"
                  />
                  <span>انزلاق تلقائي</span>
                </label>
                
                {customization.clubs_showcase.auto_slide && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-2">مدة الانزلاق (ثانية)</label>
                    <input
                      type="range"
                      min="3"
                      max="10"
                      value={customization.clubs_showcase.slide_duration / 1000}
                      onChange={(e) => updateSetting('clubs_showcase', 'slide_duration', parseInt(e.target.value) * 1000)}
                      className="w-full"
                    />
                    <div className="text-sm text-gray-500 text-center">
                      {customization.clubs_showcase.slide_duration / 1000} ثانية
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // مكون إعدادات التواصل
  const ContactSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-800">📞 قسم التواصل</h3>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={customization.contact.enabled}
            onChange={(e) => updateSetting('contact', 'enabled', e.target.checked)}
            className="mr-2"
          />
          <span>تفعيل القسم</span>
        </label>
      </div>

      {customization.contact.enabled && (
        <>
          {/* عنوان القسم */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">📝 عنوان القسم</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الرئيسي</label>
                <input
                  type="text"
                  value={customization.contact.title}
                  onChange={(e) => updateSetting('contact', 'title', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الفرعي</label>
                <textarea
                  value={customization.contact.subtitle}
                  onChange={(e) => updateSetting('contact', 'subtitle', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg h-20"
                />
              </div>
            </div>
          </div>

          {/* معلومات التواصل */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">📱 معلومات التواصل</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">البريد الإلكتروني</label>
                <input
                  type="email"
                  value={customization.contact.email}
                  onChange={(e) => updateSetting('contact', 'email', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">رقم الهاتف</label>
                <input
                  type="tel"
                  value={customization.contact.phone}
                  onChange={(e) => updateSetting('contact', 'phone', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">العنوان</label>
                <input
                  type="text"
                  value={customization.contact.address}
                  onChange={(e) => updateSetting('contact', 'address', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* روابط التواصل الاجتماعي */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">🌐 روابط التواصل الاجتماعي</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">🐦 تويتر (X)</label>
                <input
                  type="url"
                  value={customization.contact.social_links.twitter}
                  onChange={(e) => updateSetting('contact', 'social_links', {
                    ...customization.contact.social_links,
                    twitter: e.target.value
                  })}
                  className="w-full px-4 py-3 border rounded-lg"
                  placeholder="https://x.com/username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">📸 إنستغرام</label>
                <input
                  type="url"
                  value={customization.contact.social_links.instagram}
                  onChange={(e) => updateSetting('contact', 'social_links', {
                    ...customization.contact.social_links,
                    instagram: e.target.value
                  })}
                  className="w-full px-4 py-3 border rounded-lg"
                  placeholder="https://instagram.com/username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">💼 لينكد إن</label>
                <input
                  type="url"
                  value={customization.contact.social_links.linkedin}
                  onChange={(e) => updateSetting('contact', 'social_links', {
                    ...customization.contact.social_links,
                    linkedin: e.target.value
                  })}
                  className="w-full px-4 py-3 border rounded-lg"
                  placeholder="https://linkedin.com/company/name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">📺 يوتيوب</label>
                <input
                  type="url"
                  value={customization.contact.social_links.youtube}
                  onChange={(e) => updateSetting('contact', 'social_links', {
                    ...customization.contact.social_links,
                    youtube: e.target.value
                  })}
                  className="w-full px-4 py-3 border rounded-lg"
                  placeholder="https://youtube.com/@channel"
                />
              </div>
            </div>
          </div>

          {/* نموذج التواصل */}
          <div className="bg-white rounded-xl p-6 border">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold">📝 نموذج التواصل</h4>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={customization.contact.contact_form}
                  onChange={(e) => updateSetting('contact', 'contact_form', e.target.checked)}
                  className="mr-2"
                />
                <span>تفعيل النموذج</span>
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // مكون إعدادات Footer
  const FooterSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-800">📄 الفوتر</h3>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={customization.footer.enabled}
            onChange={(e) => updateSetting('footer', 'enabled', e.target.checked)}
            className="mr-2"
          />
          <span>تفعيل القسم</span>
        </label>
      </div>

      {customization.footer.enabled && (
        <>
          {/* الألوان */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">🎨 الألوان</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">لون الخلفية</label>
                <div className="flex items-center space-x-2 space-x-reverse">
                  <input
                    type="color"
                    value={customization.footer.background_color}
                    onChange={(e) => updateSetting('footer', 'background_color', e.target.value)}
                    className="w-12 h-10 rounded border"
                  />
                  <input
                    type="text"
                    value={customization.footer.background_color}
                    onChange={(e) => updateSetting('footer', 'background_color', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">لون النص</label>
                <div className="flex items-center space-x-2 space-x-reverse">
                  <input
                    type="color"
                    value={customization.footer.text_color}
                    onChange={(e) => updateSetting('footer', 'text_color', e.target.value)}
                    className="w-12 h-10 rounded border"
                  />
                  <input
                    type="text"
                    value={customization.footer.text_color}
                    onChange={(e) => updateSetting('footer', 'text_color', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* نص حقوق الطبع */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">©️ نص حقوق الطبع</h4>
            <input
              type="text"
              value={customization.footer.copyright}
              onChange={(e) => updateSetting('footer', 'copyright', e.target.value)}
              className="w-full px-4 py-3 border rounded-lg"
              placeholder="© 2024 منصة الأندية الرياضية. جميع الحقوق محفوظة."
            />
          </div>

          {/* الروابط */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">🔗 روابط Footer</h4>
            <div className="space-y-3">
              {customization.footer.links.map((link, index) => (
                <div key={index} className="grid grid-cols-5 gap-3 items-center">
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={link.text}
                      onChange={(e) => {
                        const newLinks = [...customization.footer.links];
                        newLinks[index].text = e.target.value;
                        updateSetting('footer', 'links', newLinks);
                      }}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="نص الرابط"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => {
                        const newLinks = [...customization.footer.links];
                        newLinks[index].url = e.target.value;
                        updateSetting('footer', 'links', newLinks);
                      }}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <button
                      onClick={() => {
                        const newLinks = customization.footer.links.filter((_, i) => i !== index);
                        updateSetting('footer', 'links', newLinks);
                      }}
                      className="w-full bg-red-500 text-white py-2 rounded hover:bg-red-600"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newLink = { text: 'رابط جديد', url: '/new-link' };
                  updateSetting('footer', 'links', [...customization.footer.links, newLink]);
                }}
                className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
              >
                ➕ إضافة رابط جديد
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // مكون إعدادات الشركاء
  const PartnersSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-800">🤝 قسم الشركاء</h3>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={customization.partners.enabled}
            onChange={(e) => updateSetting('partners', 'enabled', e.target.checked)}
            className="mr-2"
          />
          <span>تفعيل القسم</span>
        </label>
      </div>

      {customization.partners.enabled && (
        <>
          {/* عنوان القسم */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">📝 عنوان القسم</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الرئيسي</label>
                <input
                  type="text"
                  value={customization.partners.title}
                  onChange={(e) => updateSetting('partners', 'title', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">العنوان الفرعي</label>
                <textarea
                  value={customization.partners.subtitle}
                  onChange={(e) => updateSetting('partners', 'subtitle', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg h-20"
                />
              </div>
            </div>
          </div>

          {/* إعدادات العرض */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">📐 إعدادات العرض</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">نمط العرض</label>
                <select
                  value={customization.partners.layout}
                  onChange={(e) => updateSetting('partners', 'layout', e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg"
                >
                  <option value="grid">شبكة ثابتة</option>
                  <option value="scroll">تمرير تلقائي</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">لون الخلفية</label>
                <div className="flex items-center space-x-2 space-x-reverse">
                  <input
                    type="color"
                    value={customization.partners.background_color}
                    onChange={(e) => updateSetting('partners', 'background_color', e.target.value)}
                    className="w-12 h-10 rounded border"
                  />
                  <input
                    type="text"
                    value={customization.partners.background_color}
                    onChange={(e) => updateSetting('partners', 'background_color', e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* شعارات الشركاء */}
          <div className="bg-white rounded-xl p-6 border">
            <h4 className="font-bold mb-4">🏢 شعارات الشركاء</h4>
            <div className="space-y-4">
              {customization.partners.logos.map((partner, index) => (
                <div key={index} className="flex items-center space-x-4 space-x-reverse p-3 bg-gray-50 rounded-lg">
                  {partner.logo && (
                    <img 
                      src={`${API_BASE_URL}/api/files/${partner.logo}`}
                      alt={partner.name}
                      className="w-12 h-12 object-contain rounded border"
                    />
                  )}
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <input
                      type="text"
                      value={partner.name}
                      onChange={(e) => {
                        const newLogos = [...customization.partners.logos];
                        newLogos[index].name = e.target.value;
                        updateSetting('partners', 'logos', newLogos);
                      }}
                      className="px-3 py-2 border rounded"
                      placeholder="اسم الشريك"
                    />
                    <input
                      type="url"
                      value={partner.url || ''}
                      onChange={(e) => {
                        const newLogos = [...customization.partners.logos];
                        newLogos[index].url = e.target.value;
                        updateSetting('partners', 'logos', newLogos);
                      }}
                      className="px-3 py-2 border rounded"
                      placeholder="رابط الشريك"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            const fileId = await handleFileUpload(file, 'partner-logo');
                            const newLogos = [...customization.partners.logos];
                            newLogos[index].logo = fileId;
                            updateSetting('partners', 'logos', newLogos);
                            showNotification('تم رفع شعار الشريك! 🏢', 'success');
                          } catch (error) {
                            showNotification('فشل في رفع الشعار ❌', 'error');
                          }
                        }
                      }}
                      className="px-2 py-1 border rounded text-sm"
                    />
                  </div>
                  <button
                    onClick={() => {
                      const newLogos = customization.partners.logos.filter((_, i) => i !== index);
                      updateSetting('partners', 'logos', newLogos);
                    }}
                    className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                  >
                    🗑️
                  </button>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newPartner = { name: 'شريك جديد', url: '', logo: null };
                  updateSetting('partners', 'logos', [...customization.partners.logos, newPartner]);
                }}
                className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600"
              >
                ➕ إضافة شريك جديد
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // مكونات القوالب المتقدمة
  const AdvancedTemplatesManager = () => {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [newTemplateDescription, setNewTemplateDescription] = useState('');

    return (
      <div className="space-y-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">📚 إدارة القوالب المتقدمة</h3>
        
        {/* القالب الحالي */}
        <div className="bg-white rounded-xl p-6 border">
          <h4 className="font-bold mb-4">🎯 القالب النشط</h4>
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div>
              <h5 className="font-medium text-blue-800">{currentTemplate}</h5>
              <p className="text-sm text-blue-600">القالب المستخدم حالياً في الصفحة العامة</p>
            </div>
            <div className="flex space-x-3 space-x-reverse">
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
              >
                💾 حفظ كقالب جديد
              </button>
              <button
                onClick={() => {
                  const confirmExport = window.confirm('هل تريد تصدير القالب الحالي؟');
                  if (confirmExport) {
                    const dataStr = JSON.stringify(customization, null, 2);
                    const dataBlob = new window.Blob([dataStr], {type: 'application/json'});
                    const url = URL.createObjectURL(dataBlob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `template-${currentTemplate}-${new Date().toISOString().slice(0, 10)}.json`;
                    link.click();
                    showNotification('تم تصدير القالب بنجاح! 📁', 'success');
                  }
                }}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
              >
                📁 تصدير القالب
              </button>
            </div>
          </div>
        </div>

        {/* استيراد قالب */}
        <div className="bg-white rounded-xl p-6 border">
          <h4 className="font-bold mb-4">📁 استيراد قالب</h4>
          <input
            type="file"
            accept=".json"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const reader = new window.FileReader();
                reader.onload = (e) => {
                  try {
                    const importedData = JSON.parse(e.target.result);
                    setCustomization(importedData);
                    showNotification('تم استيراد القالب بنجاح! 📥', 'success');
                  } catch (error) {
                    showNotification('فشل في استيراد القالب - ملف غير صالح ❌', 'error');
                  }
                };
                reader.readAsText(file);
              }
            }}
            className="w-full px-4 py-3 border rounded-lg"
          />
          <p className="text-sm text-gray-500 mt-2">اختر ملف JSON للقالب المُصدر مسبقاً</p>
        </div>

        {/* القوالب المحفوظة */}
        <div className="bg-white rounded-xl p-6 border">
          <h4 className="font-bold mb-4">📋 القوالب المحفوظة</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map(template => (
              <div key={template.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium">{template.name}</h5>
                  <span className={`px-2 py-1 rounded text-xs ${
                    currentTemplate === template.id 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {currentTemplate === template.id ? '✅ نشط' : 'محفوظ'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                <div className="flex space-x-2 space-x-reverse">
                  <button
                    onClick={() => loadTemplate(template.id)}
                    disabled={currentTemplate === template.id}
                    className="flex-1 bg-blue-500 disabled:bg-gray-400 text-white py-2 rounded text-sm hover:bg-blue-600 disabled:cursor-not-allowed"
                  >
                    📥 تحميل
                  </button>
                  <button 
                    onClick={() => {
                      // معاينة سريعة للقالب
                      const previewUrl = `${window.location.origin}/?preview=true&template=${template.id}`;
                      window.open(previewUrl, '_blank', 'width=1200,height=800');
                    }}
                    className="bg-gray-500 text-white px-3 py-2 rounded text-sm hover:bg-gray-600"
                  >
                    👁️ معاينة
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`هل أنت متأكد من حذف القالب "${template.name}"؟`)) {
                        // حذف القالب
                        setTemplates(prev => prev.filter(t => t.id !== template.id));
                        showNotification(`تم حذف القالب "${template.name}" 🗑️`, 'success');
                      }
                    }}
                    className="bg-red-500 text-white px-3 py-2 rounded text-sm hover:bg-red-600"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Modal إنشاء قالب جديد */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h4 className="font-bold mb-4">💾 حفظ قالب جديد</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">اسم القالب</label>
                  <input
                    type="text"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg"
                    placeholder="مثال: القالب الاحترافي"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">وصف القالب</label>
                  <textarea
                    value={newTemplateDescription}
                    onChange={(e) => setNewTemplateDescription(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg h-20"
                    placeholder="وصف موجز للقالب وميزاته"
                  />
                </div>
              </div>
              <div className="flex space-x-3 space-x-reverse mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
                >
                  إلغاء
                </button>
                <button
                  onClick={() => {
                    if (newTemplateName.trim() && newTemplateDescription.trim()) {
                      saveAsTemplate(newTemplateName, newTemplateDescription);
                      setShowCreateModal(false);
                      setNewTemplateName('');
                      setNewTemplateDescription('');
                    } else {
                      window.alert('يرجى ملء جميع الحقول');
                    }
                  }}
                  className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600"
                >
                  💾 حفظ
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const TemplatesManager = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-800 mb-4">📚 إدارة القوالب</h3>
      
      {/* القالب الحالي */}
      <div className="bg-white rounded-xl p-6 border">
        <h4 className="font-bold mb-4">🎯 القالب الحالي</h4>
        <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
          <div>
            <h5 className="font-medium text-blue-800">{currentTemplate}</h5>
            <p className="text-sm text-blue-600">القالب النشط حالياً</p>
          </div>
          <div className="flex space-x-3 space-x-reverse">
            <button
              onClick={() => {
                const templateName = window.prompt('اسم القالب الجديد:');
                const description = window.prompt('وصف القالب:');
                if (templateName && description) {
                  saveAsTemplate(templateName, description);
                }
              }}
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
            >
              💾 حفظ كقالب جديد
            </button>
          </div>
        </div>
      </div>

      {/* القوالب المحفوظة */}
      <div className="bg-white rounded-xl p-6 border">
        <h4 className="font-bold mb-4">📋 القوالب المحفوظة</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(template => (
            <div key={template.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium">{template.name}</h5>
                <span className={`px-2 py-1 rounded text-xs ${
                  currentTemplate === template.id 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {currentTemplate === template.id ? '✅ نشط' : 'محفوظ'}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3">{template.description}</p>
              <div className="flex space-x-2 space-x-reverse">
                <button
                  onClick={() => loadTemplate(template.id)}
                  disabled={currentTemplate === template.id}
                  className="flex-1 bg-blue-500 disabled:bg-gray-400 text-white py-2 rounded text-sm hover:bg-blue-600 disabled:cursor-not-allowed"
                >
                  📥 تحميل
                </button>
                <button className="bg-gray-500 text-white px-3 py-2 rounded text-sm hover:bg-gray-600">
                  👁️
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">جاري تحميل أدوات التخصيص...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">🎨 مخصص الصفحة العامة</h2>
            <p className="text-gray-600">تخصيص شامل للصفحة الرئيسية للمنصة</p>
          </div>
          <div className="flex items-center space-x-4 space-x-reverse">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                showPreview 
                  ? 'bg-orange-500 text-white hover:bg-orange-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {showPreview ? '👁️ إخفاء المعاينة' : '👁️ معاينة مباشرة'}
            </button>
            <button
              onClick={saveCustomization}
              disabled={saving}
              className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white px-6 py-3 rounded-lg font-medium transition-all flex items-center"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                  جاري الحفظ...
                </>
              ) : (
                <>
                  <span className="text-lg ml-2">💾</span>
                  حفظ التخصيصات
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-full">
        {/* Side Panel - أدوات التخصيص */}
        <div className="w-80 bg-white border-r overflow-y-auto">
          {/* Tabs Navigation */}
          <div className="border-b">
            <div className="flex flex-col">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center space-x-3 space-x-reverse px-4 py-3 text-right hover:bg-gray-50 border-r-3 transition-all ${
                    activeSection === section.id 
                      ? 'bg-blue-50 text-blue-600 border-blue-500 font-medium' 
                      : 'text-gray-700 border-transparent'
                  }`}
                >
                  <span className="text-xl">{section.icon}</span>
                  <div className="flex-1 text-right">
                    <div className="font-medium">{section.name}</div>
                    <div className="text-xs text-gray-500">{section.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Settings Content */}
          <div className="p-4">
            {activeSection === 'general' && <GeneralSettings />}
            {activeSection === 'hero' && <HeroSettings />}
            {activeSection === 'features' && <FeaturesSettings />}
            {activeSection === 'clubs' && <ClubsSettings />}
            {activeSection === 'contact' && <ContactSettings />}
            {activeSection === 'footer' && <FooterSettings />}
            {activeSection === 'templates' && <AdvancedTemplatesManager />}
            
            {activeSection === 'partners' && <PartnersSettings />}
          </div>
        </div>

        {/* Main Content Area - المعاينة أو الإعدادات */}
        <div className="flex-1 overflow-hidden">
          {showPreview ? (
            <div className="h-full">
              <div className="bg-gray-800 text-white px-4 py-2 text-center text-sm">
                📱 معاينة مباشرة - الصفحة العامة للمنصة
              </div>
              <LivePreview />
            </div>
          ) : (
            <div className="flex items-center justify-center h-full bg-gradient-to-br from-gray-100 to-gray-200">
              <div className="text-center">
                <div className="text-8xl mb-6">🎨</div>
                <h3 className="text-2xl font-bold text-gray-700 mb-4">مخصص الصفحة العامة</h3>
                <p className="text-gray-600 mb-8 max-w-md">
                  استخدم الأدوات على اليسار لتخصيص الصفحة العامة للمنصة. 
                  انقر على "معاينة مباشرة" لرؤية التغييرات.
                </p>
                <button
                  onClick={() => setShowPreview(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-medium transition-all transform hover:scale-105"
                >
                  👁️ ابدأ المعاينة
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LandingPageCustomizer;